import ExcelJS from 'exceljs';
import { todayKey } from '../../utils/dateTime';

// Plain workbook shaped for the estimating template's paste expectations —
// no branding rows. The user copies CONTENTS into the estimate workbook, so
// layout fidelity matters more than styling.
//
// Sheets:
//   1. "IO, Comms & Devices"  — Bluebeam markup-summary list (headers row 1)
//   2. "Panels" / 3. "SCADA"  — ExE/ExF-shaped matrix: item codes in col A,
//      one 2-column pair per panel; header cells (drawing / panel name) are
//      vertical (textRotation 90) bold centered, item quantities are merged
//      2-wide centered cells — mirrors the template's J:K panel pair exactly.
//   4. "VFD & Starter"        — same list shape as sheet 1
//   5. "UnitRatesStandard"    — seed rows for the workbook's unit-rate table

const LIST_HEADERS = ['Page Label', 'Subject', 'Label', 'Count', 'Comments'];

function addListSheet(wb, name, rows) {
  const ws = wb.addWorksheet(name);
  ws.addRow(LIST_HEADERS);
  rows.forEach(r => ws.addRow([r.pageLabel, r.subject, r.label, r.count, r.comments]));
  ws.columns = [{ width: 28 }, { width: 18 }, { width: 24 }, { width: 8 }, { width: 44 }];
  return ws;
}

function addMatrixSheet(wb, name, columns, itemCodes) {
  const ws = wb.addWorksheet(name);
  const headerRow = ws.getRow(1);
  headerRow.getCell(1).value = 'Item Code';
  headerRow.getCell(1).font = { bold: true };

  columns.forEach((p, i) => {
    const c = 2 + i * 2; // each panel = a 2-column pair
    const drawingCell = headerRow.getCell(c);
    drawingCell.value = p.drawing ?? '';
    drawingCell.alignment = { textRotation: 90, horizontal: 'center', vertical: 'bottom' };
    drawingCell.font = { bold: true };
    const nameCell = headerRow.getCell(c + 1);
    nameCell.value = p.tag ?? '';
    nameCell.alignment = { textRotation: 90, horizontal: 'center', vertical: 'bottom' };
    nameCell.font = { bold: true };
  });
  headerRow.height = 110; // room for the vertical text

  itemCodes.forEach((code, r) => {
    const row = ws.getRow(2 + r);
    row.getCell(1).value = code;
    columns.forEach((p, i) => {
      const c = 2 + i * 2;
      const qty = p.items?.[code];
      row.getCell(c).value = qty > 0 ? qty : null; // blank for 0 — matches template entry style
      row.getCell(c).alignment = { horizontal: 'center' };
      ws.mergeCells(2 + r, c, 2 + r, c + 1);
    });
  });

  ws.getColumn(1).width = 26;
  columns.forEach((_, i) => {
    ws.getColumn(2 + i * 2).width = 5;
    ws.getColumn(3 + i * 2).width = 5;
  });
  return ws;
}

export default async function exportEstimateXlsx({
  rows, panels, panelItemCodes, scadaPanels, scadaItemCodes, vfdRows, unitRates, dbSlug,
}) {
  const wb = new ExcelJS.Workbook();

  addListSheet(wb, 'IO, Comms & Devices', rows || []);
  addMatrixSheet(wb, 'Panels', panels || [], panelItemCodes || []);
  addMatrixSheet(wb, 'SCADA', scadaPanels || [], scadaItemCodes || []);
  addListSheet(wb, 'VFD & Starter', vfdRows || []);

  const ur = wb.addWorksheet('UnitRatesStandard');
  ur.addRow(['Index Sheet', 'Manufacturer', 'Description', 'Model Number', 'Unit Price']);
  (unitRates || []).forEach(r => ur.addRow([r.indexSheet, r.manufacturer, r.description, r.model, null]));
  ur.columns = [{ width: 24 }, { width: 22 }, { width: 44 }, { width: 28 }, { width: 12 }];

  const stamp = todayKey().replace(/-/g, '');
  const filename = `EstimateExport_${dbSlug || 'db'}_${stamp}.xlsx`;

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
